import { z } from "zod";
import { toIsoDate } from "@common/helpers/date";
import { parseSpreadsheetDateValue } from "@common/helpers/spreadsheet";
import {
    CashItemBatchItem,
    MAX_CASH_ITEM_BATCH_SIZE,
} from "@common/schemas/domains/cashItem/cashItem.schema";
import { BankCashItemType } from "@common/types/bankCashItem";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { cashItemTypeOf } from "./cashItemAmount";
import {
    CashItemImporterRow,
    isBlankCashItemRow,
} from "./cashItemImporterRows";

export interface CashItemImportEntry {
    rowId: string;
    item: CashItemBatchItem;
}

export type InvalidCellsByRowId = ReadonlyMap<string, ReadonlySet<string>>;

export type CashItemImportValidation =
    | { isValid: true; entries: CashItemImportEntry[] }
    | {
          isValid: false;
          invalidCellsByRowId: InvalidCellsByRowId;
          message: string;
      };

interface CreateCashItemRowSchemaParams {
    isGlFirst: boolean;
    financialAccountsById: ReadonlyMap<
        number,
        Pick<FinancialAccount, "financialType">
    >;
}

export type CashItemRowSchema = ReturnType<typeof createCashItemRowSchema>;

interface ValidateCashItemImportParams {
    rows: CashItemImporterRow[];
    rowSchema: CashItemRowSchema;
}

const requiredUuid = (message: string) => z.string({ message }).uuid(message);

export function createCashItemRowSchema({
    isGlFirst,
    financialAccountsById,
}: CreateCashItemRowSchemaParams) {
    return z
        .object({
            checkNumber: z
                .string()
                .trim()
                .max(64, "Shorten the reference number"),
            date: z.string().transform((value, ctx) => {
                const date = parseSpreadsheetDateValue(value);
                if (date === null) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Enter a valid date",
                    });
                    return z.NEVER;
                }
                return toIsoDate(date);
            }),
            financialAccountId: z
                .number({ message: "Choose a bank account" })
                .int(),
            counterpartyId: requiredUuid("Choose a counterparty"),
            memo: z.string().trim().max(1000, "Shorten the memo"),
            categoryId: isGlFirst
                ? z.string().uuid().nullable()
                : requiredUuid("Choose a category"),
            accountId: requiredUuid("Choose a Cash ledger account"),
            accrualAccountId: z.string().uuid().nullable(),
            classIds: z.array(z.string().uuid()).nullable(),
            amount: z
                .number({ message: "Enter an amount" })
                .multipleOf(0.01, "Use at most two decimal places")
                .refine((amount) => amount !== 0, "Enter an amount"),
        })
        .superRefine(({ amount, financialAccountId }, ctx) => {
            const financialType =
                financialAccountsById.get(financialAccountId)?.financialType;
            if (
                amount < 0 &&
                financialType !== undefined &&
                financialType !== FinancialAccountType.CHECKING
            ) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["financialAccountId"],
                    message: "Checks can only be recorded on checking accounts",
                });
            }
        })
        .transform((row): CashItemBatchItem => {
            const amount = Math.abs(row.amount);
            return {
                type:
                    cashItemTypeOf(row.amount) === BankCashItemType.CHECK
                        ? BankCashItemType.CHECK
                        : BankCashItemType.DEPOSIT,
                financialAccountId: row.financialAccountId,
                dateOnly: row.date,
                amount,
                checkNumber: row.checkNumber === "" ? null : row.checkNumber,
                counterpartyId: row.counterpartyId,
                memo: row.memo === "" ? null : row.memo,
                splits: [
                    {
                        amount,
                        categoryId: row.categoryId ?? undefined,
                        accountId: row.accountId,
                        accrualAccountId: row.accrualAccountId ?? undefined,
                        classIds:
                            row.classIds && row.classIds.length > 0
                                ? row.classIds
                                : undefined,
                    },
                ],
            };
        });
}

export function validateCashItemImport({
    rows,
    rowSchema,
}: ValidateCashItemImportParams): CashItemImportValidation {
    const filledRows = rows
        .map((row, index) => ({ row, rowNumber: index + 1 }))
        .filter(({ row }) => !isBlankCashItemRow(row));
    if (filledRows.length > MAX_CASH_ITEM_BATCH_SIZE) {
        return {
            isValid: false,
            invalidCellsByRowId: new Map(),
            message: `You can import up to ${MAX_CASH_ITEM_BATCH_SIZE} checks and deposits at a time`,
        };
    }

    const entries: CashItemImportEntry[] = [];
    const invalidCellsByRowId = new Map<string, Set<string>>();
    const messages: string[] = [];
    for (const { row, rowNumber } of filledRows) {
        const parsed = rowSchema.safeParse(row);
        if (parsed.success) {
            entries.push({ rowId: row._id, item: parsed.data });
            continue;
        }
        const [firstIssue] = parsed.error.issues;
        messages.push(`Row ${rowNumber}: ${firstIssue.message}`);
        invalidCellsByRowId.set(
            row._id,
            new Set(parsed.error.issues.map((issue) => String(issue.path[0]))),
        );
    }

    if (messages.length === 0) {
        return { isValid: true, entries };
    }
    const attention =
        messages.length > 1 ? ` (${messages.length} rows need attention)` : "";
    return {
        isValid: false,
        invalidCellsByRowId,
        message: `${messages[0]}${attention}`,
    };
}
